import {
  Formik,
  FormikConfig,
  FormikHelpers,
  FormikProps,
  useFormikContext
} from "formik"
import PropTypes from "prop-types"
import React, { forwardRef, useImperativeHandle, useRef, useState } from "react"
import { DiscardFormStateReturn } from "./useDiscardFormState"
import useFormikDiscardFormState, {
  FormikDiscardFormStateOptions
} from "./useFormikDiscardFormState"

type DiscardRef = Pick<DiscardFormStateReturn, "discardFormState"> & {
  /** Latest Formik dirty flag; used to keep a draft typed after optimistic clear. */
  dirty: boolean
}

export type FormikSubmissionState = {
  /** Increments only after the async submit handler resolves successfully. */
  successfulSubmissionCount: number
}

/**
 * How form values + stored draft should behave around submit.
 *
 * - `preserve` — keep submitted values as the new baseline (edit/save forms).
 * - `clearOptimistic` — clear immediately, restore on error (chat composers).
 *   On success, keep any edits made after the optimistic clear.
 * - `clearOnSuccess` — keep values visible during submit (spinner UX); clear after success.
 */
export type FormStateOnSubmit = "preserve" | "clearOptimistic" | "clearOnSuccess"

type DiscardFormStateChildren<T extends any = any> =
  | React.ReactNode
  | ((props: DiscardFormStateReturn & T) => React.ReactNode)

type FormikWithDiscardFormStateProps = {
  children: DiscardFormStateChildren<FormikProps<any> & FormikSubmissionState>
  formStateOnSubmit?: FormStateOnSubmit
} & FormikDiscardFormStateOptions &
  FormikConfig<any>

export const FormikWithDiscardFormState = forwardRef<
  DiscardRef,
  FormikWithDiscardFormStateProps
>(function FormikWithDiscardFormState(
  {
    children,
    keys,
    formStateKey,
    formStateMigrationKey,
    localStorageOptions,
    onChangeDiscardFormState,
    formStateOnSubmit = "preserve",
    // The rest are formikProps.
    onSubmit,
    ...formikProps
  },
  ref
) {
  const internalRef = useRef<DiscardRef>({} as DiscardRef)
  const [successfulSubmissionCount, setSuccessfulSubmissionCount] = useState(0)

  // Expose internalRef to the outside ref, if provided.
  useImperativeHandle(ref, () => internalRef.current!, [])

  async function handleSubmit(values: any, formikHelpers: FormikHelpers<any>) {
    const preserve = formStateOnSubmit === "preserve"
    const clearOptimistic = formStateOnSubmit === "clearOptimistic"
    const valuesOverride = preserve ? values : undefined

    // Optimistic composers clear before the mutation so the UI feels instant.
    if (clearOptimistic) {
      internalRef.current.discardFormState?.(false)
    }

    try {
      const result = await onSubmit(values, formikHelpers)

      if (clearOptimistic) {
        // Already cleared before submit. Resetting again would wipe a new
        // draft typed while the request was in flight. Re-settle storage
        // only when the composer is still empty.
        if (!internalRef.current.dirty) {
          internalRef.current.discardFormState?.(false)
        }
      } else {
        internalRef.current.discardFormState?.(preserve, valuesOverride)
      }
      setSuccessfulSubmissionCount((count) => count + 1)

      return result
    } catch (error) {
      // Restore the submitted draft only when the composer is still empty.
      // Post-submit edits are a new draft and should not be overwritten.
      if (clearOptimistic && !internalRef.current.dirty) {
        formikHelpers.setValues(values, true /* shouldValidate */)
      }
      throw error
    }
  }

  return (
    <Formik {...formikProps} onSubmit={handleSubmit}>
      {(form) => (
        <FormikDiscardFormState
          ref={internalRef}
          keys={keys}
          formStateKey={formStateKey}
          formStateMigrationKey={formStateMigrationKey}
          localStorageOptions={localStorageOptions}
          onChangeDiscardFormState={onChangeDiscardFormState}
        >
          {(formikFormDiscardState) =>
            typeof children === "function"
              ? children({
                  ...form,
                  ...formikFormDiscardState,
                  successfulSubmissionCount
                })
              : children
          }
        </FormikDiscardFormState>
      )}
    </Formik>
  )
})

type FormikDiscardFormStateProps = {
  children: DiscardFormStateChildren
} & FormikDiscardFormStateOptions

const FormikDiscardFormState = forwardRef<
  DiscardRef,
  FormikDiscardFormStateProps
>(function FormikDiscardFormState(
  { children, ...formikDiscardFormStateProps },
  ref
) {
  const formikFormDiscardState = useFormikDiscardFormState(
    formikDiscardFormStateProps
  )
  const { dirty } = useFormikContext()

  useImperativeHandle(ref, () => ({
    discardFormState: formikFormDiscardState.discardFormState,
    dirty
  }))

  return typeof children === "function"
    ? children(formikFormDiscardState)
    : children
})

FormikDiscardFormState.displayName = "FormikDiscardFormState"

FormikDiscardFormState.propTypes = {
  children: PropTypes.func.isRequired
}

export default FormikDiscardFormState
